/**
 * IndexedDB-based ModelStore implementation
 * Step 4: Lightweight metadata storage for model status tracking
 *
 * Uses a single object store keyed by modelId.
 * Robust error handling for browsers without IndexedDB support.
 */
const DEFAULT_DB_NAME = 'browser-first-ai';
const DEFAULT_STORE_NAME = 'model-status';
const DB_VERSION = 1;
// === Implementation ===
/**
 * IndexedDB-based model metadata store.
 * Stores lightweight records about model status, not the actual model weights.
 */
export class IndexedDBModelStore {
    constructor(config = {}) {
        this.dbPromise = null;
        this.dbName = config.dbName ?? DEFAULT_DB_NAME;
        this.storeName = config.storeName ?? DEFAULT_STORE_NAME;
    }
    /**
     * Get or create the database connection
     */
    async getDb() {
        if (this.dbPromise) {
            return this.dbPromise;
        }
        this.dbPromise = new Promise((resolve, reject) => {
            if (typeof indexedDB === 'undefined') {
                reject(new Error('IndexedDB is not available in this environment'));
                return;
            }
            const request = indexedDB.open(this.dbName, DB_VERSION);
            request.onerror = () => {
                reject(new Error(`Failed to open IndexedDB: ${request.error?.message ?? 'Unknown error'}`));
            };
            request.onsuccess = () => {
                resolve(request.result);
            };
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                // Create object store if it doesn't exist
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'modelId' });
                }
            };
        });
        return this.dbPromise;
    }
    /**
     * Get a model's metadata record
     */
    async get(modelId) {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readonly');
                const store = tx.objectStore(this.storeName);
                const request = store.get(modelId);
                request.onerror = () => {
                    reject(new Error(`Failed to get record: ${request.error?.message ?? 'Unknown error'}`));
                };
                request.onsuccess = () => {
                    resolve(request.result ?? null);
                };
            });
        }
        catch (error) {
            // Gracefully handle IndexedDB unavailability
            console.warn('IndexedDBModelStore.get failed:', error);
            return null;
        }
    }
    /**
     * Set a model's metadata record
     */
    async set(modelId, record) {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readwrite');
                const store = tx.objectStore(this.storeName);
                const request = store.put({ ...record, modelId });
                request.onerror = () => {
                    reject(new Error(`Failed to set record: ${request.error?.message ?? 'Unknown error'}`));
                };
                request.onsuccess = () => {
                    resolve();
                };
            });
        }
        catch (error) {
            // Gracefully handle IndexedDB unavailability
            console.warn('IndexedDBModelStore.set failed:', error);
        }
    }
    /**
     * Delete a model's metadata record
     */
    async delete(modelId) {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readwrite');
                const store = tx.objectStore(this.storeName);
                const request = store.delete(modelId);
                request.onerror = () => {
                    reject(new Error(`Failed to delete record: ${request.error?.message ?? 'Unknown error'}`));
                };
                request.onsuccess = () => {
                    resolve();
                };
            });
        }
        catch (error) {
            console.warn('IndexedDBModelStore.delete failed:', error);
        }
    }
    /**
     * List all model records
     */
    async list() {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readonly');
                const store = tx.objectStore(this.storeName);
                const request = store.getAll();
                request.onerror = () => {
                    reject(new Error(`Failed to list records: ${request.error?.message ?? 'Unknown error'}`));
                };
                request.onsuccess = () => {
                    resolve(request.result ?? []);
                };
            });
        }
        catch (error) {
            console.warn('IndexedDBModelStore.list failed:', error);
            return [];
        }
    }
    /**
     * Clear all records
     */
    async clear() {
        try {
            const db = await this.getDb();
            return new Promise((resolve, reject) => {
                const tx = db.transaction(this.storeName, 'readwrite');
                const store = tx.objectStore(this.storeName);
                const request = store.clear();
                request.onerror = () => {
                    reject(new Error(`Failed to clear records: ${request.error?.message ?? 'Unknown error'}`));
                };
                request.onsuccess = () => {
                    resolve();
                };
            });
        }
        catch (error) {
            console.warn('IndexedDBModelStore.clear failed:', error);
        }
    }
}
/**
 * Create a default IndexedDBModelStore instance
 */
export function createIndexedDBModelStore(config) {
    return new IndexedDBModelStore(config);
}
//# sourceMappingURL=IndexedDBModelStore.js.map